import { useMemo } from 'react';
import {
  SectionAuthTitle,
  HorizontalButtonGroup,
  ButtonContainer,
  UseLocal,
  SignInLink,
} from './styled';
import loadable from '@loadable/component';
import Media from 'react-media';
import { Text, useTheme } from '@simplywallst/ui-core';

import {
  ContinueWithGoogle,
  ContinueWithEmail,
} from '@/components/OAuthButtons';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useOAuthButtons } from '../OAuthButtons/useOAuthButtons';

const ContinueWithApple = loadable(
  () => import('@/components/OAuthButtons/Apple/ContinueWithApple'),
  {
    resolveComponent: (components) => components.ContinueWithApple,
  }
);

const AuthenticationErrorMessages = loadable(
  () => import('../AuthenticationErrorMessages'),
  {
    resolveComponent: (c) => c.AuthenticationErrorMessages,
  }
);

interface Props {
  onClickRegister: () => void;
  onClickSignIn: () => void;
  onLoginSuccess?: () => void;
  onRegisterSuccess?: () => void;
  ticker?: string;
}

export const ContinueWithButtons = ({
  onClickRegister,
  onClickSignIn,
  onLoginSuccess,
  onRegisterSuccess,
  ticker,
}: Props) => {
  const { os, isMobile, isTablet } = useUserAgent();
  const theme = useTheme();

  const { status, error, handleApple, handleGoogle } = useOAuthButtons({
    onLoginSuccess,
    onRegisterSuccess,
  });

  const buttons = useMemo(() => {
    return {
      google: {
        component: ContinueWithGoogle,
        onSuccess: handleGoogle,
      },
      apple: {
        component: ContinueWithApple,
        onSuccess: handleApple,
      },
      email: {
        component: ContinueWithEmail,
        onClick: onClickRegister,
      },
    };
  }, [onClickRegister, handleApple, handleGoogle]);

  return (
    <>
      <SectionAuthTitle>
        {ticker ? (
          <Text
            typography={'smallStrong'}
            color={'charcoal'}
            textAlign={'center'}
          >
            Sign up for free and <br /> continue analysing {ticker}
          </Text>
        ) : (
          <Text
            typography={'smallStrong'}
            color={'charcoal'}
            textAlign={'center'}
          >
            Sign up to continue.
            <Media
              query={{ minWidth: theme.lgMin }}
              defaultMatches={isMobile || isTablet}
            >
              {(desktop) => {
                if (desktop) {
                  return <br />;
                }
                return ' ';
              }}
            </Media>
            Free forever.
          </Text>
        )}
      </SectionAuthTitle>
      <Media
        query={{ minWidth: theme.lgMin }}
        defaultMatches={isMobile || isTablet}
      >
        {(desktop) => {
          if (desktop) {
            return (
              <>
                {status === 'error' && (
                  <AuthenticationErrorMessages
                    selectorId="login-notification-error"
                    error={error}
                  />
                )}
                <ButtonContainer>
                  <buttons.google.component
                    disabled={status === 'loading'}
                    onSuccess={buttons.google.onSuccess}
                  />
                </ButtonContainer>
                {os === 'iOS' ? (
                  <ButtonContainer>
                    <buttons.apple.component
                      disabled={status === 'loading'}
                      onSuccess={buttons.apple.onSuccess}
                    />
                  </ButtonContainer>
                ) : null}
                <ButtonContainer>
                  <buttons.email.component onClick={buttons.email.onClick} />
                </ButtonContainer>
              </>
            );
          }
          return (
            <>
              <ButtonContainer>
                <buttons.google.component
                  onSuccess={buttons.google.onSuccess}
                />
              </ButtonContainer>

              <HorizontalButtonGroup>
                {os === 'iOS' ? (
                  <ButtonContainer>
                    <buttons.apple.component
                      showBtnText={false}
                      onSuccess={buttons.apple.onSuccess}
                    />
                  </ButtonContainer>
                ) : null}
                <ButtonContainer>
                  <buttons.email.component
                    showBtnText={os !== 'iOS'}
                    onClick={buttons.email.onClick}
                  />
                </ButtonContainer>
              </HorizontalButtonGroup>
            </>
          );
        }}
      </Media>

      <UseLocal>
        Already have an account?{' '}
        <SignInLink
          styleType="strongLink"
          onClick={onClickSignIn}
          data-cy-id="button-email-login"
        >
          Sign in
        </SignInLink>
      </UseLocal>
    </>
  );
};
